import type { DivergenceRow, InventoryScanRow } from "../api/rest";

/**
 * Report helpers for the inventory run page (ADR 137): divergence CSV export and
 * the per-aisle reconciliation rollup. Pure functions — the page stays thin and
 * these carry the tests.
 */

/** Canonical divergence CSV (semicolon, LF, status codes verbatim) — the
 *  operational artifact handed to warehouse ops / the WMS side. FifoDate/AgeDays
 *  (ADR 194) are blank when the snapshot carries no aging. */
export function divergencesToCsv(rows: DivergenceRow[]): string {
  // ADR 283: the review columns travel with the export — a justified divergence
  // must carry its justification wherever the sheet goes. Semicolons inside the
  // free-text justification would shift columns; they become commas.
  const lines = ["Address;Product;Expected;Counted;Status;FifoDate;AgeDays;Disposition;Justification;ReviewedBy;ReviewedAt"];
  for (const r of rows) {
    lines.push([r.address, r.product ?? "", r.expectedQty ?? "", r.countedQty ?? "", r.status,
                r.fifoDate ?? "", r.ageDays ?? "",
                r.review?.disposition ?? "",
                r.review?.justification.replace(/;/g, ",") ?? "",
                r.review?.reviewedBy ?? "", r.review?.reviewedAt ?? ""].join(";"));
  }
  return lines.join("\n") + "\n";
}

/** Address → pallet association of a count (ADR 423): one row per (address, label)
 *  among the effective counted readings the "Endereços" table lists, with the
 *  quantity and time of that pallet's LATEST reading. Quantity is a column only
 *  when the count carries one. Times are the site's wall clock in the kit's own
 *  `yyyy-MM-dd HH:mm:ss`; a reading without a time leaves the cell blank. */
export function addressPalletsToCsv(rows: InventoryScanRow[], timeZone?: string): string {
  const latest = new Map<string, InventoryScanRow>();
  for (const r of rows) {
    if (r.status !== "counted" || !r.product) continue;
    const key = `${r.address}\u0000${r.product}`;
    const prev = latest.get(key);
    if (!prev || (r.scanTs != null && (prev.scanTs == null || r.scanTs > prev.scanTs))) latest.set(key, r);
  }
  const withQty = [...latest.values()].some((r) => r.quantity != null);
  const lines = [withQty ? "Address;Pallet;Quantity;LastSeen" : "Address;Pallet;LastSeen"];
  const sorted = [...latest.values()].sort((a, b) =>
    a.address.localeCompare(b.address) || a.product.localeCompare(b.product));
  for (const r of sorted) {
    const ts = r.scanTs == null ? "" : siteWallClock(r.scanTs, timeZone);
    lines.push((withQty ? [r.address, r.product, r.quantity ?? "", ts] : [r.address, r.product, ts]).join(";"));
  }
  return lines.join("\n") + "\n";
}

function siteWallClock(iso: string, timeZone?: string): string {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
}

export interface AisleRecon {
  aisle: string;           // aisleLabel, or "__none__" for out-of-master addresses
  keys: number;            // (address, product) pairs under this aisle
  matched: number;
  qtyMismatch: number;
  missing: number;
  unexpected: number;
  accuracyPct: number;     // matched / keys * 100
}

/** Group divergence rows by aisle via the storage-location master
 *  (address → aisleLabel). Addresses absent from the master (unexpected reads
 *  on docks etc.) land in the "__none__" bucket rather than being dropped. */
export function aisleReconciliation(
  rows: DivergenceRow[], aisleByAddress: Map<string, string>,
): AisleRecon[] {
  const acc = new Map<string, AisleRecon>();
  for (const r of rows) {
    const aisle = aisleByAddress.get(r.address) ?? "__none__";
    let a = acc.get(aisle);
    if (!a) {
      a = { aisle, keys: 0, matched: 0, qtyMismatch: 0, missing: 0, unexpected: 0, accuracyPct: 0 };
      acc.set(aisle, a);
    }
    a.keys++;
    if (r.status === "match") a.matched++;
    else if (r.status === "qty_mismatch") a.qtyMismatch++;
    else if (r.status === "missing_in_ic3") a.missing++;
    else a.unexpected++;
  }
  return [...acc.values()]
    .map((a) => ({ ...a, accuracyPct: a.keys === 0 ? 0 : (100 * a.matched) / a.keys }))
    .sort((x, y) => {
      // The out-of-master catch-all always renders last, whatever the locale
      // thinks of underscores.
      if (x.aisle === "__none__") return 1;
      if (y.aisle === "__none__") return -1;
      return x.aisle.localeCompare(y.aisle);
    });
}

/** Trigger a client-side download of a text artifact.
 *
 *  `bom` prepends a UTF-8 byte-order mark. Excel reads a .csv as the system
 *  legacy codepage unless it finds one, which turns "CódigodoItem" into
 *  "CÃ³digodoItem" on the customer's machine — so any artifact meant to be
 *  opened in Excel rather than parsed by a program asks for it (ADR 241). */
export function downloadTextFile(fileName: string, text: string, mime = "text/csv",
                                 bom = false): void {
  downloadBlob(fileName, new Blob(bom ? ["﻿", text] : [text],
                                  { type: `${mime};charset=utf-8` }));
}

/** The same download, for an artifact that is bytes rather than text — no BOM,
 *  no charset, since a .xlsx is a zip and either would corrupt it. */
export function downloadBinaryFile(fileName: string, bytes: Uint8Array, mime: string): void {
  // Copy into a plain ArrayBuffer: a Uint8Array over a larger pooled buffer
  // would otherwise hand Blob the whole pool.
  downloadBlob(fileName, new Blob([bytes.slice().buffer as ArrayBuffer], { type: mime }));
}

function downloadBlob(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
